import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { FaVideo, FaArrowLeft, FaSignOutAlt, FaUserPlus, FaDoorOpen } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { boothQueueAPI } from '../../services/boothQueue';
import { listRegisteredEvents } from '../../services/events';
import AdminHeader from '../Layout/AdminHeader';
import EmployerPageTemplate from './EmployerPageTemplate';
import BoothAvailabilityInfo from './BoothAvailabilityInfo';
import DeviceTestModal from './DeviceTestModal';
import useDialogFocus from '../../hooks/useDialogFocus';
import { announceToScreenReader } from '../Accessibility/FocusManager';
import { hydrateStreamMediaUrls } from '../../utils/videoContentProcessor';
import {
  isBoothClosed,
  isBoothLinkExpired,
  isBoothNotYetOpen,
  isEventEnded,
  formatAvailabilityDate
} from '../../utils/availability';
import './BoothQueueWaiting.css';
import './BoothEmployerPage.css';

const BOOTH_OPEN_MESSAGE = 'Booth is now open. To enter the queue, please click below.';
const EVENT_CLOSED_MESSAGE = 'This event has closed.';
const BOOTH_CLOSED_MESSAGE = 'This booth is now closed. You can still view the employer page below.';
const ACTIVE_QUEUE_STATUSES = ['waiting', 'invited', 'in_meeting'];
// setTimeout overflows above ~24.8 days; the 15 s tick covers anything further out.
const MAX_TIMEOUT_MS = 2147483647;

// Rising three-note chime, distinct from the two-note message "ting" and the
// single call-invite beep in the waiting room.
function playBoothOpenChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume().catch(() => { /* ignore */ });
    [[784, 0], [988, 0.18], [1175, 0.36]].forEach(([frequency, offset]) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      const startAt = ctx.currentTime + offset;
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(0.3, startAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.45);
      oscillator.start(startAt);
      oscillator.stop(startAt + 0.45);
    });
  } catch (_e) {
    // No audio available; the dialog and announcements still carry the message.
  }
}

// Spoken aloud for everyone (not only screen reader users), as the waiting room
// does for call invitations.
function speakText(text) {
  try {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    const englishVoice = window.speechSynthesis.getVoices().find(v => v.lang.startsWith('en'));
    if (englishVoice) utterance.voice = englishVoice;
    window.speechSynthesis.speak(utterance);
  } catch (_e) { /* ignore */ }
}

function stopSpeaking() {
  try {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  } catch (_e) { /* ignore */ }
}

const extractEvent = (data) => data?.event || (data?.success && data?.data) || (data?.name ? data : null);
const extractBooth = (data) => data?.booth || (data?.success && data?.data) || (data?.name ? data : null);

/**
 * Job seeker's view of one employer (booth) outside the queue.
 *
 * The sidebar menu follows the job seeker's state (Sprint 7 and Marge's mockup):
 *   notRegistered → Register for this Event, Return to main event, Exit the event
 *   notOpen       → "Booth not open" status, Select camera & mic, Return, Exit
 *   open          → Enter Booth Queue, Select camera & mic, Return, Exit
 *   closed        → "Booth closed" status, Return, Exit
 * Once in the queue, the job seeker is on BoothQueueWaiting instead.
 *
 * When the booth's open time passes while the page is open, a dialog offers
 * "Enter Booth Queue", which goes to the booth entrance form.
 */
export default function BoothEmployerPage() {
  const { eventSlug, boothId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [booth, setBooth] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => new Date());
  const [showOpenDialog, setShowOpenDialog] = useState(false);

  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [audioInputs, setAudioInputs] = useState([]);
  const [videoInputs, setVideoInputs] = useState([]);
  const [selectedAudioId, setSelectedAudioId] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [deviceMessage, setDeviceMessage] = useState('');

  const previousStateRef = useRef(null);
  const speakTimeoutRef = useRef(null);
  const enterQueueBtnRef = useRef(null);
  const eventId = event?._id || event?.id;

  const entryPath = `/booth-queue/${encodeURIComponent(eventSlug)}/${encodeURIComponent(boothId)}/entry`;
  const eventPath = isRegistered
    ? `/events/registered/${encodeURIComponent(eventSlug)}`
    : `/event/${encodeURIComponent(eventSlug)}`;

  const fetchBooth = useCallback(async (forEventId) => {
    const res = await axios.get(`/api/booths/${boothId}`, {
      params: forEventId ? { eventId: String(forEventId) } : undefined
    });
    const found = extractBooth(res.data);
    const count = res.data?.assignedRecruitersCount ?? found?.assignedRecruitersCount;
    return found && typeof count === 'number' ? { ...found, assignedRecruitersCount: count } : found;
  }, [boothId]);

  useEffect(() => {
    let cancelled = false;

    const checkRegistration = async (evt) => {
      try {
        const res = await listRegisteredEvents({ page: 1, limit: 100 });
        return (res?.events || []).some(e => (
          (evt?._id && e._id && String(e._id) === String(evt._id))
          || (evt?.slug && e.slug && e.slug === evt.slug)
        ));
      } catch (_e) {
        const regs = user?.metadata?.registeredEvents || [];
        return regs.some(r => (
          (r.id && evt?._id && String(r.id) === String(evt._id))
          || (r.slug && evt?.slug && r.slug === evt.slug)
        ));
      }
    };

    (async () => {
      setLoading(true);
      setError('');
      try {
        const eventRes = await axios.get(`/api/events/slug/${encodeURIComponent(eventSlug)}`);
        const evt = extractEvent(eventRes.data);
        const [foundBooth, registered] = await Promise.all([
          fetchBooth(evt?._id || evt?.id),
          checkRegistration(evt)
        ]);
        if (cancelled) return;

        // Someone already queued at this booth belongs on the waiting page.
        if (registered) {
          try {
            const status = await boothQueueAPI.getQueueStatus(boothId);
            if (!cancelled && status?.success && ACTIVE_QUEUE_STATUSES.includes(status.status)) {
              navigate(`/booth-queue/${encodeURIComponent(eventSlug)}/${encodeURIComponent(boothId)}/waiting`, { replace: true });
              return;
            }
          } catch (_e) {
            // Not in this queue: stay on the employer page.
          }
        }

        setEvent(evt);
        setBooth(foundBooth);
        setIsRegistered(registered);
        setNow(new Date());
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.status === 403
          ? 'You do not have access to this employer page.'
          : 'We could not load this employer page. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [eventSlug, boothId, fetchBooth, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-check the time gates every 15 s and exactly at the open time, and pick up
  // admin edits to open/close times without a reload.
  useEffect(() => {
    if (!booth) return undefined;
    let cancelled = false;

    const refresh = async () => {
      setNow(new Date());
      try {
        const updated = await fetchBooth(eventId);
        if (!cancelled && updated) setBooth(updated);
      } catch (_e) {
        // Keep the booth we have.
      }
    };

    const tickId = setInterval(() => setNow(new Date()), 15000);
    const refreshId = setInterval(refresh, 60000);

    let openTimeoutId;
    const openAt = booth.openTime ? new Date(booth.openTime).getTime() : NaN;
    const delay = openAt - Date.now();
    if (delay > 0 && delay < MAX_TIMEOUT_MS) {
      openTimeoutId = setTimeout(() => setNow(new Date()), delay + 250);
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(tickId);
      clearInterval(refreshId);
      clearTimeout(openTimeoutId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [booth?.openTime, eventId, fetchBooth]); // eslint-disable-line react-hooks/exhaustive-deps

  const eventEnded = isEventEnded(event, now);
  const boothClosed = !!booth && (isBoothClosed(booth, now) || isBoothLinkExpired(booth, now));
  let seekerState = null;
  if (booth && event) {
    if (eventEnded || boothClosed) seekerState = 'closed';
    else if (!isRegistered) seekerState = 'notRegistered';
    else if (isBoothNotYetOpen(booth, now)) seekerState = 'notOpen';
    else seekerState = 'open';
  }

  const closedMessage = eventEnded ? EVENT_CLOSED_MESSAGE : BOOTH_CLOSED_MESSAGE;
  const opensAtLabel = booth?.openTime ? formatAvailabilityDate(booth.openTime) : '';

  // Status wording follows Marge's mockup ("Booth not open", "Booth closed").
  // Not registered has no status line there: the Register button says it.
  let statusText = '';
  if (seekerState === 'closed') statusText = 'Booth closed';
  else if (seekerState === 'notOpen') statusText = 'Booth not open';
  else if (seekerState === 'open') statusText = 'Booth is open';
  const statusDetail = seekerState === 'notOpen' && opensAtLabel ? `Opens ${opensAtLabel}` : '';

  // Announce the page once it loads, then react to state changes while it is open.
  useEffect(() => {
    if (!seekerState) return;
    const previous = previousStateRef.current;
    previousStateRef.current = seekerState;

    if (previous === null) {
      const loadStatus = seekerState === 'notRegistered'
        ? 'You are not registered for this event'
        : [statusText, statusDetail].filter(Boolean).join('. ');
      announceToScreenReader(`${booth?.name || 'Employer'} employer page. ${loadStatus}.`);
      return;
    }
    if (previous === 'notOpen' && seekerState === 'open') {
      // The dialog takes focus and is announced by its name and description.
      // The chime, live-region announcement and spoken message make sure the
      // opening is noticed even by someone not looking at the screen.
      const boothLabel = booth?.name ? `${booth.name} is now open.` : '';
      setShowOpenDialog(true);
      playBoothOpenChime();
      announceToScreenReader(`${boothLabel} ${BOOTH_OPEN_MESSAGE}`.trim());
      clearTimeout(speakTimeoutRef.current);
      // Start speaking after the chime so the two don't overlap.
      speakTimeoutRef.current = setTimeout(
        () => speakText(`${boothLabel} To enter the queue, select Enter Booth Queue.`.trim()),
        800
      );
      return;
    }
    if (seekerState === 'closed' && previous !== 'closed') {
      setShowOpenDialog(false);
      announceToScreenReader(closedMessage);
    }
  }, [seekerState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop any pending or ongoing spoken announcement when leaving the page.
  useEffect(() => () => {
    clearTimeout(speakTimeoutRef.current);
    stopSpeaking();
  }, []);

  // The site header sticks to the top, so the info bar and sidebar stick just
  // below it. Its height depends on the event logo and on zoom, so measure it.
  const pageRef = useRef(null);
  useEffect(() => {
    const page = pageRef.current;
    const header = page?.querySelector('.admin-header');
    if (!header || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => page.style.setProperty('--bep-header-height', `${header.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, [loading]);

  // "Not now" leaves the page on the sidebar's Enter Booth Queue button, so
  // keyboard and screen reader users land next to the action they postponed.
  const closeOpenDialog = () => {
    clearTimeout(speakTimeoutRef.current);
    stopSpeaking();
    setShowOpenDialog(false);
    requestAnimationFrame(() => enterQueueBtnRef.current?.focus());
  };
  const openDialog = useDialogFocus(showOpenDialog && !showDeviceModal, closeOpenDialog);

  const handleEnterQueue = () => {
    clearTimeout(speakTimeoutRef.current);
    stopSpeaking();
    setShowOpenDialog(false);
    navigate(entryPath);
  };

  const handleTestCamera = async () => {
    setDeviceMessage('');
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audios = devices.filter(d => d.kind === 'audioinput');
      const videos = devices.filter(d => d.kind === 'videoinput');
      setAudioInputs(audios);
      setVideoInputs(videos);
      setSelectedAudioId(localStorage.getItem('preferredAudioDeviceId') || audios[0]?.deviceId || '');
      setSelectedVideoId(localStorage.getItem('preferredVideoDeviceId') || videos[0]?.deviceId || '');
      setShowDeviceModal(true);
    } catch (_e) {
      // Stays on screen until the next action, so it is not time-limited.
      setDeviceMessage('Unable to access your camera and microphone. Please check your browser permissions and try again.');
    }
  };

  const handleDeviceSave = () => {
    try {
      if (selectedAudioId) localStorage.setItem('preferredAudioDeviceId', selectedAudioId);
      if (selectedVideoId) localStorage.setItem('preferredVideoDeviceId', selectedVideoId);
    } catch (_e) { /* ignore */ }
    setShowDeviceModal(false);
    setDeviceMessage('Camera and microphone preferences saved.');
  };

  const handleReturnToEvent = () => navigate(eventPath);
  const handleExitEvent = () => navigate('/dashboard');
  const handleRegister = () => navigate(`/event/${encodeURIComponent(eventSlug)}/register`);

  if (loading) {
    return (
      <div className="booth-employer-page">
        <main id="main-content" className="bep-loading" tabIndex={-1}>
          <p role="status">Loading employer page…</p>
        </main>
      </div>
    );
  }

  const isEmployerPageMode = booth?.waitingAreaMode === 'employerPage';
  const richSections = (booth?.richSections || []).filter(s => s?.contentHtml);
  const showTestCamera = seekerState === 'notOpen' || seekerState === 'open';
  const isDialogVisible = showOpenDialog && !showDeviceModal;

  return (
    <div className="booth-employer-page" ref={pageRef}>
      <div
        className="bep-page-content"
        inert={isDialogVisible ? '' : undefined}
        aria-hidden={isDialogVisible ? 'true' : undefined}
      >
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <AdminHeader brandingLogo={event?.logoUrl || event?.logo || ''} brandingLogoAlt={event?.logoAltText || ''} hideMenuToggle={true} />
        <Helmet>
          <title>{`${booth?.name || 'Employer'} Employer Page - abilityconnect`}</title>
        </Helmet>

        <div className="bep-layout">
          <aside className="bep-sidebar" aria-label="Booth options">
            <div className="bep-identity">
              <h1 className="bep-event-title">{event?.name || 'Event'}</h1>
              <h2 className="bep-booth-title">{booth?.name || 'Employer'}</h2>
            </div>

            {statusText && (
              <p className={`queue-status bep-status bep-status-${seekerState}`}>
                <span
                  className={`status-dot ${seekerState === 'open' ? 'waiting' : 'closed'}`}
                  aria-hidden="true"
                ></span>
                <span className="status-text">{statusText}</span>
                {statusDetail && <span className="bep-status-detail">{statusDetail}</span>}
              </p>
            )}

            <div className="sidebar-actions">
              {seekerState === 'notRegistered' && (
                <button type="button" className="sidebar-action-btn bep-register-btn" onClick={handleRegister}>
                  <FaUserPlus aria-hidden="true" /> Register for this Event
                </button>
              )}

              {seekerState === 'open' && (
                <button ref={enterQueueBtnRef} type="button" className="sidebar-action-btn bep-primary-btn" onClick={handleEnterQueue}>
                  <FaDoorOpen aria-hidden="true" /> Enter Booth Queue
                </button>
              )}

              {showTestCamera && (
                <button type="button" className="sidebar-action-btn camera-btn" onClick={handleTestCamera}>
                  <FaVideo aria-hidden="true" /> Select camera & mic
                </button>
              )}

              <button type="button" className="sidebar-action-btn return-btn-alt" onClick={handleReturnToEvent}>
                <FaArrowLeft aria-hidden="true" /> Return to main event
              </button>

              <button type="button" className="sidebar-action-btn exit-btn" onClick={handleExitEvent}>
                <FaSignOutAlt aria-hidden="true" /> Exit the event
              </button>
            </div>

            {deviceMessage && (
              <p className="bep-device-message" role="status">
                {deviceMessage}
                <button type="button" className="bep-dismiss-btn" onClick={() => setDeviceMessage('')}>
                  Dismiss
                </button>
              </p>
            )}
          </aside>

          <main id="main-content" className="bep-main" tabIndex={-1} aria-label={`${booth?.name || 'Employer'} employer page`}>
            {error ? (
              <div className="recruiter-end-banner booth-closed-banner" role="alert">
                <p className="recruiter-end-banner-text">{error}</p>
              </div>
            ) : (
              <>
                <BoothAvailabilityInfo booth={booth} event={event} />

                {seekerState === 'closed' && (
                  <div className="recruiter-end-banner booth-closed-banner" role="status">
                    <p className="recruiter-end-banner-text">{closedMessage}</p>
                  </div>
                )}

                {isEmployerPageMode ? (
                  <div className="waiting-employer-pane bep-employer-pane">
                    <EmployerPageTemplate booth={booth} />
                  </div>
                ) : richSections.length > 0 ? (
                  <div className="content-grid-expanded bep-sections">
                    {richSections.map((section, idx) => (
                      <div key={section._id || idx} className="content-card-expanded">
                        <div className="content-body" dangerouslySetInnerHTML={{ __html: hydrateStreamMediaUrls(section.contentHtml) }} />
                      </div>
                    ))}
                  </div>
                ) : booth?.description ? (
                  <div className="bep-description" dangerouslySetInnerHTML={{ __html: hydrateStreamMediaUrls(booth.description) }} />
                ) : (
                  <p className="bep-empty">Employer information will be available soon.</p>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {isDialogVisible && (
        <div className="bep-open-overlay">
          <div
            className="bep-open-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="booth-open-title"
            aria-describedby="booth-open-description"
            ref={openDialog.dialogRef}
            tabIndex={-1}
            onKeyDown={openDialog.onKeyDown}
          >
            <h2 id="booth-open-title" className="bep-open-title">
              {booth?.name ? `${booth.name} is now open` : 'Booth is now open'}
            </h2>
            <p id="booth-open-description" className="bep-open-text">{BOOTH_OPEN_MESSAGE}</p>
            <div className="bep-open-actions">
              <button type="button" className="bep-open-enter-btn" onClick={handleEnterQueue}>
                Enter Booth Queue
              </button>
              <button type="button" className="bep-open-later-btn" onClick={closeOpenDialog}>
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      <DeviceTestModal
        isOpen={showDeviceModal}
        onClose={() => setShowDeviceModal(false)}
        audioInputs={audioInputs}
        videoInputs={videoInputs}
        selectedAudioId={selectedAudioId}
        selectedVideoId={selectedVideoId}
        onChangeAudio={setSelectedAudioId}
        onChangeVideo={setSelectedVideoId}
        onSave={handleDeviceSave}
      />
    </div>
  );
}
